import store from '../Redux/store';
import {showSnackbar} from "../Redux/actions/snackbar";
import { hideLoadingBar, showLoadingBar } from "../Redux/actions/loading";
import {instanceConfigured, instanceError, instanceMaintenanceRedirect} from "../Redux/actions/instsance";
import {asLinkLoaded, asLinkReset, asLinkUnavailable} from "../Redux/actions/asLink";
import axios from "axios/index";
import * as Sentry from '@sentry/browser';
const packageJson = require('../../package.json');

class FatalError extends Error {}
class SnackError extends Error {}

const MAINTENANCE_FALLBACK_MESSAGE = "Este centro se encuentra en modo Mantenimiento";
const LOCAL_CORE_API = 'http://127.0.0.1:8989/api/';

/**
 * Override de localStorage usable. El host del sitio de marketing (localhost.sistemapowergym.com) se trata como ausente.
 * @param {string|null} storedHost
 * @returns {string|null}
 */
export const usableStoredHost = (storedHost) => {
    if (typeof storedHost !== 'string') {
        return null;
    }

    const trimmed = storedHost.trim();
    if (trimmed === '') {
        return null;
    }

    try {
        const url = new URL(trimmed);
        if (url.hostname.toLowerCase() === 'localhost.sistemapowergym.com') {
            return null;
        }
    } catch (e) {
        return trimmed;
    }

    return trimmed;
};

/**
 * Base de la API: override explícito, core local en localhost, o subdominio de producción.
 * @param {{hostname: string, storedHost?: string|null}} input
 * @returns {string}
 */
export const resolveApiHost = ({hostname, storedHost}) => {
    const stored = usableStoredHost(storedHost);
    if (stored) {
        return stored;
    }

    const normalized = String(hostname || '').toLowerCase();
    if (normalized === 'localhost' || normalized === '127.0.0.1') {
        return LOCAL_CORE_API;
    }

    const instanceName = normalized.split('.')[0];
    return `https://${instanceName}.sistemapowergym.com/api/`;
};

/**
 * URL http(s) si el centro está en mantenimiento y join_url es usable.
 * @param payload
 * @returns {string|null}
 */
export const maintenanceRedirectUrl = (payload) => {
    if (!payload || payload.maintenance !== true) {
        return null;
    }

    if (typeof payload.join_url !== 'string') {
        return null;
    }

    if (/^\s|\s$/.test(payload.join_url)) {
        return null;
    }

    const joinUrl = payload.join_url.trim();
    if (joinUrl === '') {
        return null;
    }

    try {
        const url = new URL(joinUrl);
        if (url.protocol !== 'http:' && url.protocol !== 'https:') {
            return null;
        }

        return joinUrl;
    } catch (e) {
        return null;
    }
};

/**
 * Guarda la pantalla de mudanza cuando hay join_url usable. No navega.
 * @param payload
 * @returns {boolean}
 */
const stageMaintenanceRedirect = (payload) => {
    const url = maintenanceRedirectUrl(payload);
    if (!url) {
        return false;
    }

    store.dispatch(instanceMaintenanceRedirect(url));
    return true;
};

/**
 * Host based on subdomains
 */
const getHost = () => {
    const storedHost = localStorage.getItem('host');
    const hostname = window.location.hostname.toLowerCase();
    const host = resolveApiHost({hostname, storedHost});

    if (!usableStoredHost(storedHost)) {
        Sentry.setTag('instanceName', hostname.split('.')[0]);
        Sentry.setTag('versionNumber', packageJson.version);
    }

    return host;
};

/**
 * Default paramters for axios
 * @type {AxiosInstance}
 */
const powergym = axios.create({
    baseURL: getHost(),
    timeout: 10000,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    }
});

/**
 *
 * @param response
 * @returns {*}
 */
const handleAPIResponse = (response) => {

    store.dispatch(hideLoadingBar());

    if (!response) {
        throw new FatalError("Error de comunicación. Compruebe su conexión a Internet.");
    }
    
    if (typeof response.data.code === 'undefined') {
        throw new FatalError("Missing status field");
    }
    
    if (typeof response.data.success === 'undefined') {
        throw new FatalError("Missing success field");
    }

    if (stageMaintenanceRedirect(response.data)) {
        return response.data;
    }

    if (response.data.maintenance === true) {
        const apiMessage = response.data.message;
        const humanError = typeof apiMessage === 'string' && apiMessage.trim()
            ? apiMessage
            : MAINTENANCE_FALLBACK_MESSAGE;

        const branch = response.data.branch || {};
        store.dispatch(instanceError({
            humanError: humanError,
            devError: humanError,
            kind: 'maintenance',
            clubName: typeof branch.name === 'string' && branch.name.trim() ? branch.name.trim() : null,
            clubPhone: typeof branch.phone === 'string' && branch.phone.trim() ? branch.phone.trim() : null
        }));

        return response.data;
    }

    if (response.data.status === 403) {
        logout();
        throw new SnackError("Por favor, inicie sesión...");
    }
    
    return response.data;
};

/**
 *
 * @param error
 * @returns {*}
 */
const handleAPIError = (error) => {
    
    store.dispatch(hideLoadingBar());

    if (! error.response) {
        return store.dispatch(instanceError({
            humanError: "No pudimos establecer conexión con el servidor por problemas en la red. Intentelo más tarde.",
            devError: error.toString(),
            kind: 'network'
        }));
    }

    if (error instanceof FatalError) {
        return store.dispatch(instanceError({
            humanError: "Hemos detectado un problema. Reintente más tarde.",
            devError: error.toString(),
            kind: 'unknown'
        }));
    }

    if (error instanceof SnackError) {
        return store.dispatch(showSnackbar(error.toString()));
    }

    if (error.response && error.response.data) {
        //We have a response
        return error.response.data;
    }
};

/**
 *
 * @returns {string}
 */
const getAuthStr = () => {
    let user = JSON.parse(localStorage.getItem('user'));
    return `?user_id=${user.id}&access_token=${user.access_token}`;
};


/**
 * Get instance data. First time hits on API endpoint and then store in storage
 * @param forceLoad
 * @returns {*}
 */
async function getInstance() {
    try {
        const res = await powergym.get("instance");

        const responseData = handleAPIResponse(res);

        if (responseData && maintenanceRedirectUrl(responseData)) {
            return;
        }

        if (responseData.maintenance === true) {
            return responseData;
        }

        if (typeof responseData.branch === 'undefined') {
            Sentry.captureMessage('Error on server branch');
            throw new FatalError('Error on server branch');
        }
        
        store.dispatch(instanceConfigured({
            ...responseData.branch,
            host: getHost()
        }));

        return responseData;

    } catch (error) {
        handleAPIError(new FatalError(error));
    }
}

/**
 * Login
 * @param dni
 * @param password
 * @returns {Promise<Response>}
 */
async function login(dni, password) {
    try {
        store.dispatch(showLoadingBar());

        const loginData = {
            dni,
            password
        };

        const res = await powergym.post(`auth`, loginData);
        const responseData = handleAPIResponse(res);

        if (responseData.success !== true || typeof responseData.data.user === 'undefined') {
            throw new SnackError(res.message || "Login error");
        }

        localStorage.setItem('user', JSON.stringify({
            ...responseData.data.user,
            dni: dni
        }));

        Sentry.setUser({ username: dni })

        return responseData;

    } catch (error) {
        return handleAPIError(error);
    }
}

/**
 * Logout
 */
function logout() {
    // remove user from local storage to log user out
    localStorage.removeItem('user');
    store.dispatch(asLinkReset());
}


/**
 *
 * @returns {Promise<Response>}
 */
async function profile() {
    try {
        const res = await powergym.get(`me${getAuthStr()}`);
        const data = handleAPIResponse(res);
        // Puente a la cuenta nueva: el gate de rutas privadas lee este estado del store.
        if (data && data.success) {
            store.dispatch(data.as_link ? asLinkLoaded(data.as_link) : asLinkUnavailable());
        }
        return data;
    } catch (error) {
        return handleAPIError(error);
    }
}

/**
 * Token de precarga + URL del Auth Service para mandar al socio a crear/usar su cuenta nueva.
 * @returns {Promise<Response>}
 */
async function linkAccountPrefill() {
    try {
        const res = await powergym.get(`link-account/prefill${getAuthStr()}`);
        return handleAPIResponse(res);
    } catch (error) {
        return handleAPIError(error);
    }
}

/**
 * Entrega al core la afirmación firmada que devolvió el Auth Service (#assertion= en /vincular).
 * Errores de negocio vienen con `success:false` y `reason` (invalid_assertion, wrong_user,
 * staff_not_allowed, email_taken).
 * @param assertion
 * @returns {Promise<Response>}
 */
async function linkAccount(assertion) {
    try {
        const res = await powergym.post(`link-account${getAuthStr()}`, { assertion });
        const data = handleAPIResponse(res);
        if (data && data.success && data.as_link) {
            store.dispatch(asLinkLoaded(data.as_link));
        }
        return data;
    } catch (error) {
        return handleAPIError(error);
    }
}


/**
 *
 * @returns {Promise<Response>}
 */
async function myBookings() {
    try {
        const res = await powergym.get(`my-bookings${getAuthStr()}`);
        return handleAPIResponse(res);
    } catch (error) {
        return handleAPIError(error);
    }
}

/**
 *
 * @returns {Promise<Response>}
 */
async function routine() {
    try {
        const res = await powergym.get(`routine${getAuthStr()}`);
        return handleAPIResponse(res);
    } catch (error) {
        return handleAPIError(error);
    }
}

/**
 *
 * @returns {Promise<Response>}
 */
async function getSchedules() {
    try {
        const res = await powergym.get(`schedules${getAuthStr()}`);
        return handleAPIResponse(res);
    } catch (error) {
        return handleAPIError(error);
    }
}

/**
 *
 * @returns {Promise<Response>}
 */
async function getOnlineBookingsPerSchedule() {
    try {
        const res = await powergym.get(`online-bookings-by-schedule${getAuthStr()}`);
        return handleAPIResponse(res);
    } catch (error) {
        return handleAPIError(error);
    }
}


/**
 *
 * @param schedule_id
 * @param date
 * @returns {Promise<Response>}
 */
async function getTimetables(schedule_id, date) {
    try {
        const res = await powergym.get(`timetables${getAuthStr()}&schedule_id=${schedule_id}&date=${date}`);
        return handleAPIResponse(res);
    } catch (error) {
        return handleAPIError(error);
    }
}


const book = async ({timetable_id, date} = {}) => {
    try {
        const res = await powergym.post(`book${getAuthStr()}&timetable_id=${timetable_id}&date=${date}`);
        return handleAPIResponse(res);
    } catch (error) {
        return handleAPIError(error);
    }
};

/**
 *
 * @param id
 * @returns {Promise<AxiosResponse<any>>}
 */
const removeBooking = async (id) => {
    try {
        const res = await powergym.delete(`bookings/${id}${getAuthStr()}`);
        return handleAPIResponse(res);
    } catch (error) {
        return handleAPIError(error);
    }
};


export const PGService = {
    login,
    logout,
    profile,
    linkAccountPrefill,
    linkAccount,
    myBookings,
    routine,
    getInstance,
    getSchedules,
    getOnlineBookingsPerSchedule,
    getTimetables,
    removeBooking,
    book,
};
